import { defaultTo, find, flow, get, isEqual, overSome, replace } from 'lodash/fp'

import AuthApi from 'core/service/distributor/auth-api'
import { getDistributorProfile } from 'core/service/distributor/distributor-info'
import { gaPhrase } from 'core/service/pdf-generation/utils/agent'
import { setUserAdvisor } from 'identity/actions'
import {
  getAgent,
  getIdentityProfile,
  getIdentityUserAdvisor,
  getSelectedOffice,
  getUserAgentType,
} from 'identity/selectors'
import { updateSellerProfile } from 'quick-quote/actions'
import { getVersion } from 'quick-quote/selectors'
import { getSellerInfo } from 'quick-quote/selectors/partner-selector'
import { store as qqStore } from 'store'

const mappingProfile = function(state, user, agentInfo) {
  let profile = getIdentityProfile(state)
  const bancOffice = getSelectedOffice(state)
  return getDistributorProfile(profile, agentInfo, bancOffice)
}

const mappingSellerInfo = function(state, user, agentInfo, profile) {
  const bancOffice = getSelectedOffice(state)
  const distributorGroup = get('distributorGroup')(user)
  const isAgent = distributorGroup === 'AGENT'
  // const isLB = flow(get('type.code'), overSome([isEqual('LB'), isEqual('STAFF')]))(agentInfo)
  const version = getVersion(state)
  const userAgentType = getUserAgentType(state)

  const seller = {}
  seller.lbCode = get('lbCode')(profile)
  seller.brokerCode = get('agentCode')(profile)
  seller.brokerTitle = ''
  seller.brokerFirstName = flow(get('given_name'), defaultTo(''))(profile)
  seller.brokerLastName = flow(get('family_name'), defaultTo(''))(profile)
  seller.brokerLicenseNo = flow(get('basicLicenseNumber'), defaultTo(''))(profile)
  seller.brokerDepartmentCode = isAgent ? '' : flow(get('branchCode'), defaultTo(''))(bancOffice)
  seller.brokerDepartmentName = isAgent
    ? gaPhrase(get('ga')(profile))
    : flow(get('branchName'), defaultTo(''))(bancOffice)
  seller.brokerCountyCode = isAgent ? '' : flow(get('areaCode'), defaultTo(''))(bancOffice)
  seller.brokerCountyName = isAgent
    ? flow(get('unitCode'), defaultTo(''))(profile)
    : flow(get('areaName'), defaultTo(''))(bancOffice)
  seller.brokerEmail = flow(get('email'), defaultTo(''))(profile)
  seller.brokerMobile = flow(get('mobile'), defaultTo(''))(profile)
  seller.icLicense = flow(get('singleLicenseNumber'), defaultTo(''))(profile)

  seller.brokerSupervisorCode = ''
  seller.brokerSupervisorTitle = ''
  seller.brokerSupervisorFirstName = ''
  seller.brokerSupervisorLastName = ''

  seller.brokerSectorCode = flow(get('regionCode'), defaultTo(''))(bancOffice)
  seller.brokerSectorName = flow(get('regionName'), defaultTo(''))(bancOffice)

  seller.brokerManagerCode = flow(get('managerCode'), defaultTo(''))(bancOffice)
  seller.brokerManagerFirstName = flow(get('managerFirstName'), defaultTo(''))(bancOffice)

  seller.brokerFinanceCode = flow(get('fseCode'), defaultTo(''))(bancOffice)
  seller.brokerFinanceFirstName = flow(get('fseFirstName'), defaultTo(''))(bancOffice)
  seller.brokerFinanceLastName = flow(get('fseLastName'), defaultTo(''))(bancOffice)

  seller.adviserCode = flow(get('agentCode'), defaultTo(''))(profile)
  seller.adviserTitle = ''
  seller.adviserFirstName = flow(get('given_name'), defaultTo(''))(profile)
  seller.adviserLastName = flow(get('family_name'), defaultTo(''))(profile)
  seller.adviserDepartmentCode = flow(get('ga.code'), defaultTo(''))(profile)
  seller.adviserDepartmentName = flow(get('ga.name'), defaultTo(''))(profile)

  seller.distributionChannel = 'AZ'
  seller.distributorGroup = distributorGroup
  seller.appVersion = version.appVersion
  seller.userAgentType = userAgentType

  return seller
}

const mappingSellerForEsub = function(state, user, agentInfo, profile) {
  const agent = getAgent(state)
  const distributorGroup = get('distributorGroup')(user)
  const isAgent = distributorGroup === 'AGENT'
  const isLB = flow(get('type.code'), overSome([isEqual('LB'), isEqual('STAFF')]))(agentInfo)
  const version = getVersion(state)
  const userAgentType = getUserAgentType(state)

  const seller = {}
  seller.lbCode = get('advisorId')(agentInfo)
  seller.brokerCode = get('advisorCode')(agentInfo)
  seller.brokerTitle = ''
  // seller.brokerFirstName = flow(get('party.firstName'), defaultTo(''))(agentInfo)
  // seller.brokerLastName = flow(get('party.lastName'), defaultTo(''))(agentInfo)
  seller.brokerFirstName = flow(get('profile.given_name'), defaultTo(''))(user)
  seller.brokerLastName = flow(get('profile.family_name'), defaultTo(''))(user)
  seller.brokerLicenseNo = flow(
    get('party.registrations'),
    find((registration) => get('typeCode')(registration) === 'BASIC'),
    get('id'),
    defaultTo('')
  )(agentInfo)
  seller.brokerDepartmentCode = isAgent ? '' : flow(get('office.branch.code'), defaultTo(''))(agentInfo)
  seller.brokerDepartmentName = isAgent
    ? gaPhrase(get('office.ga')(agentInfo))
    : flow(get('office.branch.name'), defaultTo(''))(agentInfo)
  seller.brokerCountyCode = isAgent ? '' : flow(get('office.area.code'), defaultTo(''))(agentInfo)
  seller.brokerCountyName = isAgent
    ? seller.brokerCode.split('-')[0]
    : flow(get('office.area.name'), defaultTo(''))(agentInfo)
  seller.brokerEmail = flow(
    get('contactMethods.emailContact'),
    find((contract) => get('type')(contract) === 'email'),
    get('url'),
    defaultTo('')
  )(agentInfo)
  seller.brokerMobile = flow(
    get('contactMethods.phoneContacts'),
    find((contract) => get('type')(contract) === 'mobile'),
    get('localNumber'),
    defaultTo('')
  )(agentInfo)
  seller.icLicense = flow(
    get('party.registrations'),
    find((registration) => get('typeCode')(registration) === 'SINGLE LIC'),
    get('id'),
    defaultTo('')
  )(agentInfo)

  seller.brokerSupervisorCode = flow(get('recommender.advisorCode'), defaultTo(''))(agent)
  seller.brokerSupervisorTitle = ''
  seller.brokerSupervisorFirstName = flow(get('recommender.firstName'), defaultTo(''))(agent)
  seller.brokerSupervisorLastName = flow(get('recommender.lastName'), defaultTo(''))(agent)

  seller.brokerSectorCode = flow(get('office.region.code'), defaultTo(''))(agent)
  seller.brokerSectorName = flow(get('office.region.name'), defaultTo(''))(agent)

  seller.brokerManagerCode = isLB ? flow(get('office.manager.code'), replace(/-/g, ''), defaultTo(''))(agent) : ''
  seller.brokerManagerFirstName = isLB ? flow(get('agent.office.manager.firstName'), defaultTo(''))(agent) : ''

  seller.brokerFinanceCode = isLB ? flow(get('fse.advisorCode'), defaultTo(''))(agent) : ''
  seller.brokerFinanceFirstName = isLB ? flow(get('fse.firstName'), defaultTo(''))(agent) : ''
  seller.brokerFinanceLastName = isLB ? flow(get('fse.lastName'), defaultTo(''))(agent) : ''

  seller.adviserCode = flow(get('profile.agent_code'), defaultTo(''))(user)
  seller.adviserTitle = ''
  seller.adviserFirstName = flow(get('profile.given_name'), defaultTo(''))(user)
  seller.adviserLastName = flow(get('profile.family_name'), defaultTo(''))(user)
  seller.adviserDepartmentCode = flow(get('office.ga.code'), defaultTo(''))(user)
  seller.adviserDepartmentName = flow(get('office.ga.name'), defaultTo(''))(user)

  seller.distributionChannel = 'AZ'
  seller.distributorGroup = distributorGroup
  seller.appVersion = version.appVersion
  seller.userAgentType = userAgentType

  return seller
}

const seller = async function(user) {
  const state = qqStore.getState()
  let sellerProfile = getSellerInfo(state)
  let agentInfo

  if (sellerProfile?.userInformation) {
    agentInfo = sellerProfile.userInformation
  } else {
    agentInfo = getIdentityUserAdvisor(state)

    if (!agentInfo) {
      agentInfo = await AuthApi.getUserInformation(user)

      if (agentInfo) {
        qqStore.dispatch(setUserAdvisor(agentInfo))
      }
    }
  }

  const profile = mappingProfile(state, user, agentInfo)
  const sellerInformation = mappingSellerInfo(state, user, agentInfo, profile)

  qqStore.dispatch(updateSellerProfile({ userInformation: agentInfo, profile, sellerInformation }))

  return [sellerInformation, profile]
}

const sellerForEsub = async function(user) {
  const state = qqStore.getState()

  let agentInfo = getIdentityUserAdvisor(state)
  if (!agentInfo) {
    agentInfo = await AuthApi.getUserInformation(user)

    if (agentInfo) {
      qqStore.dispatch(setUserAdvisor(agentInfo))
    }
  }

  const profile = mappingProfile(state, user, agentInfo)
  const sellerInformation = mappingSellerForEsub(state, user, agentInfo, profile)

  return [sellerInformation, profile]
}

export const mainFields = {
  seller,
  sellerForEsub,
}
